import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { usePatientEncounters } from '@/hooks/usePatientEncounters'
import { PatientVisitsTable } from '@/components/patient/PatientVisitsTable'

/**
 * A patient's Epic visit history, as a popup (z8nrz7e0rb).
 *
 * Replaces the inline card this page used to carry. Visits are reference
 * material — a coordinator checks whether Epic has admitted someone, then gets
 * on with the procedure — so they earn a button, not permanent vertical space
 * above the work.
 *
 * The table is `PatientVisitsTable`, the same one the Patient History dialog
 * uses, so the three surfaces showing visits cannot disagree about a column or
 * a format.
 *
 * ## Why `sticky`, and why the height cap
 *
 * Both are inherited from what the Patient History dialog learned the hard way:
 * the TABLE scrolls, not the dialog. Without it a patient with many visits grew
 * the content past the viewport and — because the content is centred with
 * `translate-y-[-50%]` — it was clipped at the TOP as well as the bottom,
 * taking the title and close button off screen with no way to scroll them back.
 * Hence `max-h-[85vh] + flex-col + overflow-hidden` here and `sticky` on the
 * table, which gives it `min-h-0 flex-1 overflow-y-auto` plus a sticky header.
 */
export function PatientVisitsDialog({
  open,
  onOpenChange,
  mrn,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  mrn: string
}) {
  // Gated on `open`: the whole point of moving this behind a button is that a
  // patient page no longer fetches admissions nobody asked to see.
  const encounters = usePatientEncounters(mrn, open)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Width matched to the Patient History dialog, which shows this same
          table: seven columns wrap badly in the default dialog width, and two
          surfaces showing one table at different widths is a difference with
          no reason behind it. */}
      <DialogContent className='flex max-h-[85vh] w-[min(64rem,95vw)] max-w-none flex-col gap-4 overflow-hidden'>
        <DialogHeader className='shrink-0'>
          <DialogTitle>
            Patient Visit History
            {encounters.data && encounters.data.length > 0 ? (
              <span className='ml-1.5 text-sm font-normal text-muted-foreground'>
                ({encounters.data.length})
              </span>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            Hospital admissions Epic has sent for this MRN. Read-only.
          </DialogDescription>
        </DialogHeader>

        <PatientVisitsTable
          rows={encounters.data}
          loading={encounters.isPending}
          error={encounters.error}
          sticky
        />
      </DialogContent>
    </Dialog>
  )
}
